import { Inject } from '@odinjs/odin';
import { FieldAPI, FormAPI, InputAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnCancel, OnMount, OnSubmit, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { FragmentNames } from '../../../../codegen';
import { SuspendGlycemicAliases } from '../../../../codegen/constants/aliases/suspend-glycemic-aliases';
import { TimeUtils } from '../../../../utils/time-utils';
import BaseController from '../../../BaseController';
import MedicationService, { CreatePrescriptionScheduleChangeRequest } from '../../../adep/services/medication-service';
import { GlycemicService } from './glycemic-service';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters } from '../../../../utils/parameters';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import moment from 'moment';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ApplicationRoute } from '../../../../constants/routes';
import TimelineContext from '../../services/timeline-service';

@Controller({ fragment: FragmentNames.SUSPEND_GLYCEMIC })
export default class GlycemicSuspendController extends BaseController {

  @Inject(SuspendGlycemicAliases.FORM_GLYCEMIC_SUSPEND)
  formApi!: FormAPI;

  @Inject(SuspendGlycemicAliases.FIELD_TEXT_AREA_JUSTIFICATION)
  justificationFieldApi!: FieldAPI;

  @OnMount(SuspendGlycemicAliases.DT_CURRENT_TIME_INPUT)
  onMountTimeInput(event: MountEvent): void {
    TimeUtils.setDateToInput(event.target as InputAPI);
  }

  @OnMount(SuspendGlycemicAliases.LAYOUT_SUSPEND_GLYCEMIC)
  onMountLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.context.set({
      encounterId,
      patientId
    });
  }

  @OnMount(SuspendGlycemicAliases.FORM_GLYCEMIC_SUSPEND)
  prepareContextOnFormMount(): void {
    this.handleJustificationFieldValidation();
    const param5 = FoundationUtils.findModuleParam('ADEP#1113', ADEP_E1113Parameters.REQUIRE_JUSTIFICATION_ON_SUSPEND_ITEM);
    const { groupingNumber, prescriptionsNumber, pdoPrn, itemType } = TimelineContext.get();
    this.context.set({
      ...GlycemicService.instance,
      groupingNumber: groupingNumber || '',
      isPdoPrn: pdoPrn ? 'S' : 'N',
      itemType,
      param5,
      prescriptionsNumber,
    });

  }

  @OnSubmit(SuspendGlycemicAliases.FORM_GLYCEMIC_SUSPEND)
  onSubmitForm(): void {
    this.onSubmitSuspendAction();
  }

  @OnCancel(SuspendGlycemicAliases.FORM_GLYCEMIC_SUSPEND)
  onCancelForm(): void {
    this.historyService.back();
  }

  @OnValidate(SuspendGlycemicAliases.FORM_GLYCEMIC_SUSPEND)
  onValidateForm(/**valid: ValidateEvent */): void {
    const validateJustification = this.handleJustificationFieldValidation();
    if (validateJustification) {
      this.justificationFieldApi.setValidators([{ name: 'required' }])
    } else {
      this.justificationFieldApi.setValidators([]);
    }
  }

  onSubmitSuspendAction(): void {
    this.formApi.setLoading(true);
    this.formApi.setLocked(true);
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();

    MedicationService.createPrescriptionScheduleChange(this.createSuspendObject()).then(() => {
      MedicationService.showToast('label.medication-suspend.suspend-success', ToastLevel.SUCCESS)
      this.formApi.setLoading(false);
      applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
        encounterId,
        patientId,
      });

      applicationContext.clearNavigationStack();
    }).catch(response => {
      this.formApi.setLoading(false);
      response.body?.errors.forEach((message: string ) => MedicationService.showToast(message, ToastLevel.WARNING))
    });

  }

  handleJustificationFieldValidation(): boolean {
    const hasValidation = FoundationUtils.findModuleParam('ADEP#1113', ADEP_E1113Parameters.REQUIRE_JUSTIFICATION_ON_SUSPEND_ITEM) === 'S';

    if(!hasValidation) {
      this.justificationFieldApi.removeValidator('medication-suspend-field-justification-validador');
      return true;
    }
    return false;
  }

  private createSuspendObject(): CreatePrescriptionScheduleChangeRequest {
    const { justification, observation } = this.formApi.getRecord();
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const param22 = FoundationUtils.findModuleParam('ADEP#1113', ADEP_E1113Parameters.PRESCRIPTION_HOUR_QUANTITY);

    return {
      administrationRouteType: '',
      changeType: 5,
      componentsDescription: '',
      encounterNumber: encounterId as number,
      eventDate: moment(this.context.get('currentTime')).format(ISO_FULL_DATE_FORMAT),
      functionCode: 9129,
      glucoseProteinSequence: this.context.get('glycemicProtocolSequence'),
      groupingNumber: this.context.get('groupingNumber'),
      intervalCode: this.context.get('intervalCode'),
      isGenerateAll: 'N',
      isPalm: 'S',
      itemCode: this.context.get('procedureCode'),
      itemDescription: this.context.get('protocolDescription'),
      itemObservationDescription: '',
      itemQuantity: this.context.get('doseQuantity'),
      itemSequence: this.context.get('sequenceProcedure'),
      itemType: this.context.get('itemType'),
      justifyDescription: justification as string,
      messageDescriptionResponse: '',
      observationDescription: observation as string,
      pdoPrn: this.context.get('pdoPrn'),
      prescriptionHourQuantity: param22 as number,
      prescriptionNumber: this.context.get('prescriptionNumber'),
      prescriptionsNumber: this.context.get('prescriptionNumber'),
      procedureInternalSequenceNumber: this.context.get('internalProcedureSequence'),
      scheduleSequence: this.context.get('scheduleSequenceNumber'),
      suspendPrescribedMaterial: '',
      unitMeasureCode: '',
      uoMCodeQuantityAdministered: ''
    }
  }
}

